import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { OrderRecommendation } from '../../../services/item-details.models';

export interface RecommendationSelectEvent {
    recommendation: OrderRecommendation;
    quantity: number | null;
}

export type RecommendationStockState = {
    status: 'checking' | 'ok' | 'error';
    avl?: number | null;
};

type ForecastHorizon = '7d' | '14d' | '30d';

@Component({
    selector: 'app-v2-recommendations',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    template: `
        <section
            class="v2-card v2-card--recommendations"
            [class.v2-card--recommendations-collapsed]="!expanded"
        >
            <div class="v2-rec-heading">
                <button
                    type="button"
                    class="v2-rec-toggle"
                    (click)="toggleExpanded()"
                    [attr.aria-expanded]="expanded"
                    aria-controls="v2-rec-body"
                >
                    <i
                        class="pi pi-chevron-down v2-rec-chevron"
                        [class.v2-rec-chevron--collapsed]="!expanded"
                        aria-hidden="true"
                    ></i>
                    <div class="v2-rec-heading-text">
                        <h3>
                            <i class="pi pi-star" aria-hidden="true"></i>
                            Suggested for customer
                            <span class="v2-rec-beta">BETA</span>
                        </h3>
                        <p class="v2-rec-sub" *ngIf="expanded">
                            Personalized recommendations based on your purchase history &amp; demand patterns
                        </p>
                    </div>
                </button>
                <div class="v2-rec-heading-right">
                    <span class="v2-rec-count" *ngIf="!loading && displayRows.length">
                        {{ displayRows.length }}{{ totalCount > displayRows.length ? '+' : '' }}
                    </span>
                    <button
                        type="button"
                        class="v2-rec-next"
                        *ngIf="expanded && displayRows.length > 1"
                        (click)="scrollNext($event)"
                        aria-label="Next suggestions"
                        [pTooltip]="'Next'"
                        tooltipPosition="left"
                    >
                        <i class="pi pi-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            <div class="v2-rec-body" id="v2-rec-body" *ngIf="expanded">
                <div class="v2-rec-state" *ngIf="loading">Loading suggestions…</div>
                <div class="v2-rec-state v2-rec-state--error" *ngIf="!loading && error && !displayRows.length">
                    {{ error }}
                </div>
                <div class="v2-rec-state" *ngIf="!loading && !error && !displayRows.length">
                    No info for this customer
                </div>

                <div class="v2-rec-strip" *ngIf="!loading && displayRows.length">
                <div #recList class="v2-rec-list" role="list">
                    <article
                        class="v2-rec-item"
                        role="listitem"
                        *ngFor="let rec of displayRows; trackBy: trackByItem"
                    >
                        <!-- 1. Compact header: identity | NEXT n DAYS forecast -->
                        <div class="v2-rec-header">
                            <div class="v2-rec-meta">
                                <span class="v2-rec-rank">{{ rec.rank }}</span>
                                <strong class="v2-rec-id">{{ rec.itemId }}</strong>
                            </div>

                            <div class="v2-rec-fc">
                                <div class="v2-rec-fc-top">
                                    <span class="v2-rec-fc-title">{{ forecastHeading(rec) }}</span>
                                    <i
                                        class="pi pi-info-circle v2-rec-fc-info"
                                        [pTooltip]="infoTooltip(rec)"
                                        tooltipPosition="left"
                                        [tooltipDisabled]="!infoTooltip(rec)"
                                        aria-label="Recommendation details"
                                    ></i>
                                </div>
                                <div class="v2-rec-fc-nums">
                                    <span class="v2-rec-fc-metric">
                                        <strong>{{ formatNum(forecastExpected(rec)) }}</strong>
                                        <em>expected</em>
                                    </span>
                                    <span class="v2-rec-fc-metric">
                                        <strong>{{ formatNum(forecastUpTo(rec)) }}</strong>
                                        <em>up to</em>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- 2. Description (max 2 lines) -->
                        <div class="v2-rec-name" [attr.title]="nameOf(rec) || null">
                            {{ nameOf(rec) || '—' }}
                        </div>

                        <!-- 3. Purchase history — reserved row so footers align across cards -->
                        <div class="v2-rec-history">
                            <ng-container *ngIf="hasHistory(rec)">
                                <span class="v2-rec-hist" *ngIf="reorderLine(rec) as line">
                                    <i class="pi pi-sync" aria-hidden="true"></i>{{ line }}
                                </span>
                                <span class="v2-rec-hist" *ngIf="boughtLine(rec) as line">
                                    <i class="pi pi-shopping-cart" aria-hidden="true"></i>{{ line }}
                                </span>
                                <span class="v2-rec-hist" *ngIf="lastLine(rec) as line">
                                    <i class="pi pi-clock" aria-hidden="true"></i>{{ line }}
                                </span>
                            </ng-container>
                        </div>

                        <!-- 4. Divider + action row: Price | Stock | Suggested Qty | Select -->
                        <div class="v2-rec-footer">
                            <div class="v2-rec-footer-metrics">
                                <div class="v2-rec-price" aria-readonly="true">
                                    <span class="v2-rec-foot-label">Price</span>
                                    <strong>{{ formatMoney(priceOf(rec)) }}</strong>
                                </div>
                                <div
                                    class="v2-rec-stock"
                                    [ngClass]="'v2-rec-stock--' + stockHealth(rec)"
                                    [attr.title]="stockTitle(rec)"
                                >
                                    <span class="v2-rec-foot-label">{{ stockFootLabel(rec) }}</span>
                                    <strong class="v2-rec-stock-value">
                                        <i *ngIf="stockIconClass(rec)" [class]="stockIconClass(rec)" aria-hidden="true"></i>
                                        <span>{{ stockValueText(rec) }}</span>
                                    </strong>
                                </div>
                                <div class="v2-rec-qty" aria-readonly="true">
                                    <span class="v2-rec-foot-label">Suggested Qty</span>
                                    <strong>{{ formatSuggestedQty(rec) }}</strong>
                                </div>
                            </div>
                            <button type="button" class="v2-rec-select-btn" (click)="onSelect(rec)">
                                <i class="pi pi-check" aria-hidden="true"></i>
                                Select
                            </button>
                        </div>
                    </article>
                </div>
            </div>
            </div>
        </section>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
            }

            .v2-card--recommendations {
                border: 1px solid var(--border-color, var(--p-surface-border));
                border-radius: 0.6rem;
                padding: 0.4rem 0.6rem 0.35rem;
                background: var(--surface-card, var(--p-surface-0));
                color: var(--text-color, var(--p-text-color));
                max-height: none;
                overflow: visible;
            }

            .v2-card--recommendations-collapsed {
                padding-bottom: 0.3rem;
            }

            .v2-rec-heading {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
                min-height: 42px;
                margin-bottom: 0.3rem;
            }
            .v2-card--recommendations-collapsed .v2-rec-heading {
                margin-bottom: 0;
            }
            .v2-rec-toggle {
                display: flex;
                align-items: center;
                gap: 0.35rem;
                min-width: 0;
                flex: 1 1 auto;
                margin: 0;
                padding: 0;
                border: none;
                background: transparent;
                color: inherit;
                font: inherit;
                text-align: left;
                cursor: pointer;
            }
            .v2-rec-chevron {
                flex-shrink: 0;
                font-size: 0.72rem;
                color: var(--text-secondary, var(--p-text-muted-color));
                transition: transform 0.15s ease;
            }
            .v2-rec-chevron--collapsed {
                transform: rotate(-90deg);
            }
            .v2-rec-heading-text {
                min-width: 0;
            }
            .v2-rec-heading h3 {
                margin: 0;
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                font-size: 0.92rem;
                font-weight: 800;
                line-height: 1.15;
                color: var(--primary-color, #ca8a04);
            }
            .v2-rec-heading h3 .pi {
                font-size: 0.78rem;
            }
            .v2-rec-beta {
                font-size: 0.55rem;
                font-weight: 800;
                letter-spacing: 0.06em;
                padding: 0.08rem 0.32rem;
                border-radius: 999px;
                color: #9a3412;
                background: #ffedd5;
                border: 1px solid #fdba74;
                line-height: 1.2;
            }
            .v2-rec-sub {
                margin: 0.08rem 0 0;
                font-size: 0.68rem;
                font-weight: 600;
                line-height: 1.2;
                color: var(--text-secondary, var(--p-text-muted-color));
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .v2-rec-heading-right {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                flex-shrink: 0;
            }
            .v2-rec-count {
                font-size: 0.65rem;
                font-weight: 800;
                padding: 0.1rem 0.4rem;
                border-radius: 999px;
                color: #9a3412;
                background: #ffedd5;
                border: 1px solid #fdba74;
            }
            .v2-rec-next {
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 999px;
                border: 1px solid var(--border-color, var(--p-surface-border));
                background: var(--surface-section, var(--p-surface-50));
                color: var(--text-color, var(--p-text-color));
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 0;
            }
            .v2-rec-next:hover {
                color: var(--primary-color, #ca8a04);
            }

            .v2-rec-state {
                font-size: 0.78rem;
                font-weight: 600;
                color: var(--text-secondary, var(--p-text-muted-color));
                padding: 0.15rem 0;
            }
            .v2-rec-state--error {
                color: var(--red-500, #ef4444);
            }

            .v2-rec-list {
                display: flex;
                flex-direction: row;
                flex-wrap: nowrap;
                align-items: stretch;
                gap: 0.65rem;
                margin: 0;
                padding: 0.1rem 0.05rem 0.2rem;
                width: 100%;
                overflow-x: auto;
                overflow-y: hidden;
                scroll-snap-type: x proximity;
                scroll-behavior: smooth;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: thin;
            }
            .v2-rec-list::-webkit-scrollbar {
                height: 5px;
            }
            .v2-rec-list::-webkit-scrollbar-thumb {
                background: color-mix(in srgb, var(--text-color, #888) 30%, transparent);
                border-radius: 999px;
            }

            .v2-rec-item {
                flex: 0 0 clamp(18.5rem, 28vw, 20.5rem);
                width: clamp(18.5rem, 28vw, 20.5rem);
                scroll-snap-align: start;
                display: flex;
                flex-direction: column;
                gap: 0.2rem;
                height: 158px;
                min-height: 158px;
                max-height: 158px;
                box-sizing: border-box;
                overflow: hidden;
                padding: 0.45rem 0.55rem 0;
                border: 1px solid #d1d5db;
                border-radius: 0.5rem;
                background: #fff;
                color: var(--text-color, #111827);
            }

            .v2-rec-header {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                gap: 0.4rem;
                flex-shrink: 0;
                min-height: 2.35rem;
            }
            .v2-rec-meta {
                display: flex;
                align-items: center;
                gap: 0.3rem;
                min-width: 0;
                flex: 1;
                padding-top: 0.05rem;
            }
            .v2-rec-rank {
                flex-shrink: 0;
                width: 1.2rem;
                height: 1.2rem;
                border-radius: 0.25rem;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 0.7rem;
                font-weight: 900;
                color: #111827;
                background: #facc15;
            }
            .v2-rec-id {
                font-size: 0.84rem;
                font-weight: 900;
                line-height: 1;
                flex-shrink: 0;
            }

            .v2-rec-fc {
                flex-shrink: 0;
                text-align: right;
                min-width: 9.5rem;
            }
            .v2-rec-fc-top {
                display: inline-flex;
                align-items: center;
                justify-content: flex-end;
                gap: 0.25rem;
                width: 100%;
            }
            .v2-rec-fc-title {
                font-size: 0.6rem;
                font-weight: 900;
                letter-spacing: 0.06em;
                color: #c2410c;
                line-height: 1.1;
            }
            .v2-rec-fc-info {
                font-size: 0.68rem;
                color: #9ca3af;
                cursor: help;
            }
            /* Mockup: one row — "8.38 expected" | "13.40 up to" under NEXT 7 DAYS */
            .v2-rec-fc-nums {
                display: flex;
                flex-direction: row;
                flex-wrap: nowrap;
                align-items: baseline;
                justify-content: flex-end;
                gap: 0.55rem;
                margin-top: 0.12rem;
                white-space: nowrap;
            }
            .v2-rec-fc-metric {
                display: inline-flex;
                align-items: baseline;
                gap: 0.2rem;
            }
            .v2-rec-fc-metric strong {
                font-size: 0.9rem;
                font-weight: 900;
                color: #c2410c;
                font-variant-numeric: tabular-nums;
                line-height: 1;
            }
            .v2-rec-fc-metric em {
                font-style: normal;
                font-size: 0.62rem;
                font-weight: 600;
                color: #6b7280;
                line-height: 1;
            }

            .v2-rec-name {
                flex-shrink: 0;
                font-size: 0.74rem;
                font-weight: 800;
                line-height: 1.25;
                color: #111827;
                overflow: hidden;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                height: 1.85rem;
                min-height: 1.85rem;
                max-height: 1.85rem;
            }

            .v2-rec-history {
                display: flex;
                flex-wrap: nowrap;
                align-items: center;
                gap: 0.5rem;
                overflow: hidden;
                flex-shrink: 0;
                height: 1rem;
                min-height: 1rem;
                max-height: 1rem;
            }
            .v2-rec-hist {
                display: inline-flex;
                align-items: center;
                gap: 0.18rem;
                font-size: 0.62rem;
                font-weight: 650;
                line-height: 1.1;
                color: #6b7280;
                white-space: nowrap;
            }
            .v2-rec-hist .pi {
                font-size: 0.58rem;
                color: #d97706;
            }

            .v2-rec-footer {
                display: flex;
                align-items: stretch;
                gap: 0;
                width: auto;
                align-self: stretch;
                margin: auto -0.55rem 0;
                padding: 0 0.4rem 0.28rem 0;
                border-top: 1px solid #e5e7eb;
                min-height: 2.15rem;
                flex-shrink: 0;
            }
            .v2-rec-footer-metrics {
                display: flex;
                align-items: flex-end;
                gap: 0.5rem;
                flex: 1 1 auto;
                min-width: 0;
                padding: 0.22rem 0.4rem 0.28rem 0.55rem;
            }
            .v2-rec-foot-label {
                display: block;
                font-size: 0.58rem;
                font-weight: 700;
                color: #9ca3af;
                line-height: 1.1;
                margin-bottom: 0.12rem;
            }
            .v2-rec-price {
                flex: 0 1 auto;
                min-width: 0;
                pointer-events: none;
                user-select: text;
            }
            .v2-rec-price strong {
                display: block;
                font-size: 0.82rem;
                font-weight: 900;
                color: #111827;
                font-variant-numeric: tabular-nums;
                line-height: 1.15;
            }
            .v2-rec-stock {
                flex: 0 1 auto;
                min-width: 3.75rem;
                max-width: 6.5rem;
            }
            .v2-rec-stock--oos,
            .v2-rec-stock--checking,
            .v2-rec-stock--error {
                max-width: 7.25rem;
            }
            .v2-rec-stock-value {
                display: inline-flex;
                align-items: center;
                gap: 0.22rem;
                font-size: 0.78rem;
                font-weight: 900;
                font-variant-numeric: tabular-nums;
                line-height: 1.15;
                white-space: nowrap;
            }
            .v2-rec-stock--oos .v2-rec-stock-value,
            .v2-rec-stock--checking .v2-rec-stock-value,
            .v2-rec-stock--error .v2-rec-stock-value {
                white-space: normal;
                line-height: 1.05;
            }
            .v2-rec-stock-value .pi {
                font-size: 0.72rem;
                line-height: 1;
            }
            .v2-rec-stock--checking .v2-rec-stock-value,
            .v2-rec-stock--error .v2-rec-stock-value,
            .v2-rec-stock--oos .v2-rec-stock-value {
                color: #6b7280;
                font-size: 0.68rem;
                font-weight: 750;
            }
            .v2-rec-stock--ok .v2-rec-stock-value {
                color: #15803d;
            }
            .v2-rec-stock--low .v2-rec-stock-value {
                color: #d97706;
            }
            .v2-rec-stock--veryLow .v2-rec-stock-value {
                color: #b91c1c;
            }
            .v2-rec-qty {
                flex: 0 1 auto;
                min-width: 0;
                pointer-events: none;
                user-select: text;
            }
            .v2-rec-qty strong {
                display: block;
                font-size: 0.82rem;
                font-weight: 900;
                color: #111827;
                font-variant-numeric: tabular-nums;
                line-height: 1.15;
            }
            .v2-rec-select-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 0.28rem;
                flex: 0 0 auto;
                align-self: stretch;
                margin: 0.28rem 0 0;
                min-width: 4.6rem;
                padding: 0 0.75rem;
                border: none;
                border-radius: 0.35rem;
                background: #facc15;
                color: #111827;
                font-size: 0.72rem;
                font-weight: 800;
                white-space: nowrap;
                cursor: pointer;
                line-height: 1;
                box-shadow: none;
            }
            .v2-rec-select-btn .pi {
                font-size: 0.7rem;
            }
            .v2-rec-select-btn:hover {
                background: #eab308;
            }
            .v2-rec-select-btn:active {
                background: #ca8a04;
            }

            /* Dark mode */
            :host-context(.app-dark) .v2-rec-item,
            :host-context(html.app-dark) .v2-rec-item {
                background: var(--surface-overlay, var(--p-surface-800, #1f2937));
                border-color: color-mix(in srgb, #fff 14%, transparent);
                color: var(--text-color, #f3f4f6);
            }
            :host-context(.app-dark) .v2-rec-name,
            :host-context(html.app-dark) .v2-rec-name,
            :host-context(.app-dark) .v2-rec-price strong,
            :host-context(html.app-dark) .v2-rec-price strong,
            :host-context(.app-dark) .v2-rec-qty strong,
            :host-context(html.app-dark) .v2-rec-qty strong {
                color: var(--text-color, #f3f4f6);
            }
            :host-context(.app-dark) .v2-rec-hist,
            :host-context(html.app-dark) .v2-rec-hist,
            :host-context(.app-dark) .v2-rec-fc-nums,
            :host-context(html.app-dark) .v2-rec-fc-nums,
            :host-context(.app-dark) .v2-rec-foot-label,
            :host-context(html.app-dark) .v2-rec-foot-label {
                color: var(--text-secondary, #9ca3af);
            }
            :host-context(.app-dark) .v2-rec-footer,
            :host-context(html.app-dark) .v2-rec-footer {
                border-top-color: color-mix(in srgb, #fff 12%, transparent);
            }
            :host-context(.app-dark) .v2-rec-count,
            :host-context(html.app-dark) .v2-rec-count,
            :host-context(.app-dark) .v2-rec-beta,
            :host-context(html.app-dark) .v2-rec-beta {
                color: #fdba74;
                background: rgba(249, 115, 22, 0.22);
                border-color: rgba(249, 115, 22, 0.5);
            }
            :host-context(.app-dark) .v2-rec-next,
            :host-context(html.app-dark) .v2-rec-next {
                background: var(--surface-overlay, var(--p-surface-800, #1f2937));
            }
            :host-context(.app-dark) .v2-rec-stock--checking .v2-rec-stock-value,
            :host-context(html.app-dark) .v2-rec-stock--checking .v2-rec-stock-value,
            :host-context(.app-dark) .v2-rec-stock--error .v2-rec-stock-value,
            :host-context(html.app-dark) .v2-rec-stock--error .v2-rec-stock-value,
            :host-context(.app-dark) .v2-rec-stock--oos .v2-rec-stock-value,
            :host-context(html.app-dark) .v2-rec-stock--oos .v2-rec-stock-value {
                color: #9ca3af;
            }
            :host-context(.app-dark) .v2-rec-stock--ok .v2-rec-stock-value,
            :host-context(html.app-dark) .v2-rec-stock--ok .v2-rec-stock-value {
                color: #4ade80;
            }
            :host-context(.app-dark) .v2-rec-stock--low .v2-rec-stock-value,
            :host-context(html.app-dark) .v2-rec-stock--low .v2-rec-stock-value {
                color: #fb923c;
            }
            :host-context(.app-dark) .v2-rec-stock--veryLow .v2-rec-stock-value,
            :host-context(html.app-dark) .v2-rec-stock--veryLow .v2-rec-stock-value {
                color: #f87171;
            }
            :host-context(.app-dark) .v2-rec-fc-title,
            :host-context(html.app-dark) .v2-rec-fc-title,
            :host-context(.app-dark) .v2-rec-fc-metric strong,
            :host-context(html.app-dark) .v2-rec-fc-metric strong {
                color: #fb923c;
            }
            :host-context(.app-dark) .v2-rec-fc-metric em,
            :host-context(html.app-dark) .v2-rec-fc-metric em {
                color: var(--text-secondary, #9ca3af);
            }
        `
    ]
})
export class V2RecommendationsComponent implements OnInit, OnChanges {
    @Input() recommendations: OrderRecommendation[] = [];
    @Input() loading = false;
    @Input() error = '';
    @Input() maxVisible = 10;
    @Input() currencySymbol = '€';
    /** Live stock for visible cards only (keyed by itemId). */
    @Input() stockByItemId: Record<string, RecommendationStockState> = {};

    @Output() select = new EventEmitter<RecommendationSelectEvent>();

    @ViewChild('recList') private recList?: ElementRef<HTMLDivElement>;

    displayRows: OrderRecommendation[] = [];
    totalCount = 0;
    expanded = true;

    private qtyByKey = new Map<string, number | null>();

    ngOnInit(): void {
        this.refreshDisplayRows();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['recommendations'] || changes['maxVisible']) {
            this.refreshDisplayRows();
        }
    }

    private refreshDisplayRows(): void {
        const list = Array.isArray(this.recommendations)
            ? this.recommendations.filter(
                  (r): r is OrderRecommendation => !!r && r.itemId != null && String(r.itemId).trim() !== ''
              )
            : [];
        this.totalCount = list.length;
        const cap = Math.max(1, Number(this.maxVisible) || 10);
        this.displayRows = list.slice(0, cap);
        this.syncQtyDefaults();
    }

    trackByItem = (_index: number, rec: OrderRecommendation): string =>
        `${rec?.rank ?? 0}-${String(rec?.itemId ?? '')}`;

    itemKey = (rec: OrderRecommendation): string => `${rec?.rank ?? 0}-${String(rec?.itemId ?? '')}`;

    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }

    scrollNext(event?: Event): void {
        event?.stopPropagation();
        const el = this.recList?.nativeElement;
        if (!el) {
            return;
        }
        const card = el.querySelector('.v2-rec-item') as HTMLElement | null;
        const step = card ? card.offsetWidth + 10 : 300;
        el.scrollBy({ left: step, behavior: 'smooth' });
    }

    qtyOf(rec: OrderRecommendation): number | null {
        const key = this.itemKey(rec);
        if (this.qtyByKey.has(key)) {
            return this.qtyByKey.get(key) ?? null;
        }
        return this.defaultQty(rec);
    }

    formatSuggestedQty(rec: OrderRecommendation): string {
        const q = this.qtyOf(rec);
        if (q == null || !Number.isFinite(Number(q))) {
            return '—';
        }
        return this.formatNum(q, 2);
    }

    nameOf(rec: OrderRecommendation): string {
        const meta = rec?.metadata ?? {};
        const keys = [
            'PRODUCT_DESCRIPTION',
            'product_description',
            'ITEM_DESCRIPTION',
            'item_description',
            'DESCRIPTION',
            'description',
            'FUDS',
            'ITDS'
        ];
        for (const key of keys) {
            const val = meta[key];
            if (val != null && String(val).trim() !== '') {
                return String(val).trim();
            }
        }
        return '';
    }

    hasHistory(rec: OrderRecommendation): boolean {
        return !!(this.reorderLine(rec) || this.boughtLine(rec) || this.lastLine(rec));
    }

    /** Stock health vs suggested qty for the action-row indicator. */
    stockHealth(rec: OrderRecommendation): 'checking' | 'error' | 'ok' | 'low' | 'veryLow' | 'oos' {
        const state = this.stockStateOf(rec);
        if (!state || state.status === 'checking') {
            return 'checking';
        }
        if (state.status === 'error') {
            return 'error';
        }
        const avl = Number(state.avl);
        if (!Number.isFinite(avl)) {
            return 'error';
        }
        if (avl <= 0) {
            return 'oos';
        }
        const suggested = Number(this.qtyOf(rec));
        if (!Number.isFinite(suggested) || suggested <= 0) {
            return 'ok';
        }
        if (avl < suggested) {
            return 'veryLow';
        }
        if (avl < suggested * 2) {
            return 'low';
        }
        return 'ok';
    }

    stockFootLabel(rec: OrderRecommendation): string {
        // Keep label row height aligned with Price / Qty; OOS uses the value text only.
        return this.stockHealth(rec) === 'oos' ? '\u00a0' : 'Stock';
    }

    stockValueText(rec: OrderRecommendation): string {
        const health = this.stockHealth(rec);
        if (health === 'checking') {
            return 'Checking…';
        }
        if (health === 'error') {
            return 'Cannot verify';
        }
        if (health === 'oos') {
            return 'Out of stock';
        }
        const avl = Number(this.stockStateOf(rec)?.avl);
        return this.formatStockQty(avl);
    }

    stockIconClass(rec: OrderRecommendation): string {
        switch (this.stockHealth(rec)) {
            case 'ok':
                return 'pi pi-check-circle';
            case 'low':
            case 'veryLow':
                return 'pi pi-exclamation-triangle';
            case 'oos':
                return 'pi pi-ban';
            case 'checking':
            case 'error':
                return '';
            default:
                return '';
        }
    }

    stockTitle(rec: OrderRecommendation): string {
        switch (this.stockHealth(rec)) {
            case 'checking':
                return 'Checking stock…';
            case 'error':
                return 'Cannot verify stock';
            case 'oos':
                return 'Out of stock';
            case 'veryLow':
                return 'Less than suggested qty';
            case 'low':
                return 'Getting low';
            case 'ok':
                return 'Comfortably available';
            default:
                return '';
        }
    }

    private stockStateOf(rec: OrderRecommendation): RecommendationStockState | undefined {
        const id = String(rec?.itemId ?? '').trim();
        return id ? this.stockByItemId?.[id] : undefined;
    }

    private formatStockQty(n: number): string {
        if (!Number.isFinite(n)) {
            return '—';
        }
        if (Math.abs(n - Math.round(n)) < 1e-9) {
            return String(Math.round(n));
        }
        return this.formatNum(n, 2);
    }

    /** Prefer 7D; if missing, fall back 14D → 30D (label updates to match). */
    private resolveHorizon(rec: OrderRecommendation): ForecastHorizon {
        if (this.hasQty(rec?.p50QuantityNext7d) || this.hasQty(rec?.p90QuantityNext7d)) {
            return '7d';
        }
        if (this.hasQty(rec?.p50QuantityNext14d) || this.hasQty(rec?.p90QuantityNext14d)) {
            return '14d';
        }
        if (this.hasQty(rec?.p50QuantityNext30d) || this.hasQty(rec?.p90QuantityNext30d)) {
            return '30d';
        }
        return '7d';
    }

    private hasQty(value: unknown): boolean {
        if (value == null || value === '') {
            return false;
        }
        const n = Number(value);
        return Number.isFinite(n) && n > 0;
    }

    forecastHeading(rec: OrderRecommendation): string {
        const h = this.resolveHorizon(rec);
        if (h === '14d') {
            return 'NEXT 14 DAYS';
        }
        if (h === '30d') {
            return 'NEXT 30 DAYS';
        }
        return 'NEXT 7 DAYS';
    }

    forecastExpected(rec: OrderRecommendation): number | null {
        const h = this.resolveHorizon(rec);
        if (h === '14d') {
            return this.numOrNull(rec.p50QuantityNext14d);
        }
        if (h === '30d') {
            return this.numOrNull(rec.p50QuantityNext30d);
        }
        return this.numOrNull(rec.p50QuantityNext7d);
    }

    forecastUpTo(rec: OrderRecommendation): number | null {
        const h = this.resolveHorizon(rec);
        if (h === '14d') {
            return this.numOrNull(rec.p90QuantityNext14d);
        }
        if (h === '30d') {
            return this.numOrNull(rec.p90QuantityNext30d);
        }
        return this.numOrNull(rec.p90QuantityNext7d);
    }

    /** Friendly tooltip for (i) — history lines only, no ML scores. */
    infoTooltip(rec: OrderRecommendation): string {
        const bits = [this.reorderLine(rec), this.boughtLine(rec), this.lastLine(rec)].filter(Boolean);
        const name = this.nameOf(rec);
        if (!bits.length && !name) {
            return '';
        }
        const lines = [
            name ? `${rec.itemId} — ${name}` : String(rec.itemId),
            ...bits,
            `${this.forecastHeading(rec)}: ${this.formatNum(this.forecastExpected(rec))} expected · ${this.formatNum(this.forecastUpTo(rec))} up to`
        ];
        return lines.join('\n');
    }

    /** Display-only price: 7D → 14D → 30D → metadata PRICE. */
    priceOf(rec: OrderRecommendation): number | null {
        const fromHorizon =
            this.numOrNull(rec?.expectedPriceNext7d) ??
            this.numOrNull(rec?.expectedPriceNext14d) ??
            this.numOrNull(rec?.expectedPriceNext30d);
        if (fromHorizon != null) {
            return fromHorizon;
        }
        const meta = rec?.metadata ?? {};
        return this.numOrNull(meta['PRICE'] ?? meta['price']);
    }

    private numOrNull(value: unknown): number | null {
        if (value == null || value === '') {
            return null;
        }
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }

    private reasons(rec: OrderRecommendation): string[] {
        return (rec?.reasons ?? [])
            .map((r) => String(r ?? '').trim())
            .filter((r) => r.length > 0 && !/^personalized recommendation$/i.test(r));
    }

    reorderLine(rec: OrderRecommendation): string {
        for (const r of this.reasons(rec)) {
            const m = r.match(/every\s+([\d]+)\s*[–\-]\s*([\d]+)\s*days?/i);
            if (m) {
                return `Every ${m[1]}–${m[2]}d`;
            }
            const m2 = r.match(/every\s+(\d+)\s*days?/i);
            if (m2) {
                return `Every ${m2[1]}d`;
            }
            const m3 = r.match(/median\s+(\d+)/i);
            if (m3) {
                return `Every ~${m3[1]}d`;
            }
        }
        const med = this.historyNum(rec, 'median_reorder_days');
        if (med != null) {
            return `Every ~${Math.round(med)}d`;
        }
        return '';
    }

    boughtLine(rec: OrderRecommendation): string {
        for (const r of this.reasons(rec)) {
            const m = r.match(/bought in\s+(\d+)\s+of\s+(?:the\s+last\s+)?(\d+)\s+orders?/i);
            if (m) {
                return `${m[1]}/${m[2]} orders`;
            }
        }
        return '';
    }

    lastLine(rec: OrderRecommendation): string {
        for (const r of this.reasons(rec)) {
            const m = r.match(/last bought\s+(\d+)\s*days?\s*ago/i);
            if (m) {
                return `Last bought ${m[1]}d ago`;
            }
        }
        return '';
    }

    private historyNum(rec: OrderRecommendation, key: string): number | null {
        const raw = rec?.metadata?.['forecast_history'];
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            return null;
        }
        const val = (raw as Record<string, unknown>)[key];
        return this.numOrNull(val);
    }

    private defaultQty(rec: OrderRecommendation): number | null {
        const n = this.forecastExpected(rec);
        if (n == null || n <= 0) {
            return null;
        }
        return Math.round(n * 100) / 100;
    }

    private syncQtyDefaults(): void {
        const next = new Map<string, number | null>();
        for (const rec of this.displayRows) {
            const key = this.itemKey(rec);
            next.set(key, this.qtyByKey.has(key) ? (this.qtyByKey.get(key) ?? null) : this.defaultQty(rec));
        }
        this.qtyByKey = next;
    }

    formatNum(value: unknown, digits = 2): string {
        if (value == null || value === '') {
            return '—';
        }
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return '—';
        }
        return n.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: digits
        });
    }

    formatMoney(value: unknown): string {
        if (value == null || value === '') {
            return '—';
        }
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return '—';
        }
        return `${this.currencySymbol}${n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }

    onSelect(rec: OrderRecommendation): void {
        const qty = this.qtyOf(rec);
        this.select.emit({
            recommendation: rec,
            quantity: qty != null && Number(qty) > 0 ? Number(qty) : null
        });
    }
}
